
import Vue from "vue";
import { mapGetters, mapActions } from "vuex";
import { logError } from "@/lib/utils/logger";
import yomisukeIcon from "@/assets/img/manuscript-analysis/yomisuke_theme.png";

export interface CatIconProps {
  size?: "small" | "medium" | "large";
  writerType?: string;
  clickable?: boolean;
}

export default Vue.extend({
  name: "CatIcon",
  props: {
    size: {
      type: String,
      default: "medium",
      validator: (value: string) => ["small", "medium", "large"].includes(value),
    },
    // 結果画面ではnullの場合ヨミスケを表示
    writerType: {
      type: String,
      default: null,
    },
    clickable: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      showFallback: false,
      imageLoadError: false,
    };
  },

  computed: {
    ...mapGetters("writerTypeResultModule", {
      writerTypeFromStore: "writerType",
      serializedResultFromStore: "serializedResult",
      writerTypeLoading: "isLoading",
      isRandomFromStore: "isRandom",
      lastFetched: "lastFetched",
    }),
    ...mapGetters("assistantCatModule", {
      selectedCatType: "selectedType",
      selectedCatSerializedResult: "serializedResult",
    }),
    ...mapGetters("assistNavigationModule", {
      currentContent: "currentContent",
      currentTab: "currentTab",
    }),

    sizeClass(): string {
      return `cat-icon--${this.size}`;
    },

    isLoading(): boolean {
      if (this.writerType) {
        return false;
      }
      return this.writerTypeLoading;
    },

    effectiveWriterType(): any {
      // 優先順位：
      // 1. props.writerType（明示的指定）- MyPage、モーダル等から渡される
      if (this.writerType) {
        return { tendency: this.writerType };
      }

      // 2. assistantCatModule.selectedType（ユーザーが選択した猫）
      if (this.selectedCatType) {
        return { tendency: this.selectedCatType };
      }

      // 3. writerTypeResultModule.writerType（診断結果/ランダム）- 現状の挙動
      return this.writerTypeFromStore;
    },

    effectiveSerializedResult(): string | null {
      // propsで渡された猫の場合は該当なし
      if (this.writerType) {
        return null;
      }

      // 選択された猫のserializedResult
      if (this.selectedCatSerializedResult) {
        return this.selectedCatSerializedResult;
      }

      // 診断結果のserializedResult
      return this.serializedResultFromStore;
    },

    shouldShowYomisukeIcon(): boolean {
      return this.currentTab === "assist" && this.currentContent === "result" && !this.writerType;
    },

    computedIconSrc(): string {
      const baseUrl = process.env.VUE_APP_NOLA_WEB;

      // 結果画面でwriterTypeがnullの場合はヨミスケを表示
      if (this.shouldShowYomisukeIcon) {
        return yomisukeIcon;
      }

      const tendency = this.effectiveWriterType.tendency?.toLowerCase();
      if (!tendency) {
        return "";
      }
      return `${baseUrl}/writer-type-indicator/images/result/cats/${tendency}.png`;
    },

    computedAltText(): string {
      // 結果画面でwriterTypeがnullの場合はヨミスケのalt textを表示
      if (this.shouldShowYomisukeIcon) {
        return "ヨミスケアイコン";
      }

      const { tendency } = this.effectiveWriterType;
      if (!tendency) {
        return "猫アイコン";
      }
      return `${tendency}タイプの猫アイコン`;
    },
  },

  methods: {
    ...mapActions("writerTypeResultModule", {
      fetchMyResult: "fetchMyResult",
    }),

    handleImageError(event: Event): void {
      const target = event.target as HTMLImageElement;

      if (this.showFallback) {
        return;
      }

      target.style.display = "none";
      this.showFallback = true;
      this.imageLoadError = true;
    },

    async initializeWriterType(): Promise<void> {
      const shouldFetch = this.lastFetched === null;

      if (shouldFetch) {
        try {
          await this.fetchMyResult();
        } catch (error) {
          logError({ module: "CatIcon", action: "initializeWriterType" }, "API取得エラー", error);
        }
      }
    },

    handleClick(): void {
      // clickableがfalseの場合は早期リターン
      if (!this.clickable) {
        return;
      }

      // ヨミスケアイコンの場合はクリック無効
      if (this.shouldShowYomisukeIcon) {
        return;
      }

      const { effectiveSerializedResult } = this;
      const { tendency: currentCatType } = this.effectiveWriterType;

      // serializedResultがある場合の処理
      if (effectiveSerializedResult) {
        // 現在表示中の猫と診断結果の猫が同一の場合のみ、診断結果画面に遷移
        const storedCatType = this.writerTypeFromStore?.tendency;
        if (currentCatType === storedCatType) {
          try {
            const baseUrl = process.env.VUE_APP_NOLA_WEB;
            const writerTypeUrl = `${baseUrl}/writer-type-indicator/result/${effectiveSerializedResult}`;
            window.open(writerTypeUrl, "_blank");
          } catch (error) {
            logError({ module: "CatIcon", action: "handleClick" }, "作家診断結果ページへの遷移に失敗", error);
          }
          return;
        }
      }

      // serializedResultがない場合、または選択中の猫が診断結果と異なる場合は作家診断LPに遷移
      try {
        const baseUrl = process.env.VUE_APP_NOLA_WEB;
        const writerTypeUrl = `${baseUrl}/writer-type-indicator/`;
        window.open(writerTypeUrl, "_blank");
      } catch (error) {
        logError({ module: "CatIcon", action: "handleClick" }, "作家診断ページへの遷移に失敗", error);
      }
    },
  },

  async mounted() {
    if (!this.writerType) {
      await this.initializeWriterType();
    }
  },
});
